'use client';

import { useEffect } from 'react';
import Script from 'next/script';

/**
 * Suppresses harmless WebSocket HMR connection errors in the console.
 * These errors occur when accessing the dev server via production domain (carchina.mainfact.ai)
 * but the WebSocket connection fails because the dev server is running locally.
 * 
 * This component runs a script IMMEDIATELY (before React hydration) to catch errors early,
 * and also sets up additional error handlers after React mounts.
 */
export function SuppressHMRErrors() {
  useEffect(() => {
    // Additional error handling after React mounts (backup)
    const originalError = console.error;
    const originalWarn = console.warn;
    
    // Override console.error to filter out WebSocket HMR errors
    console.error = function(...args: any[]) {
      const message = args.join(' ');
      
      // Suppress specific WebSocket HMR errors - catch various formats
      if (
        (message.includes('WebSocket connection') || 
         message.includes('WebSocket') || 
         message.includes('web-socket') ||
         message.includes('ws://') ||
         message.includes('wss://')) &&
        (message.includes('_next/webpack-hmr') || 
         message.includes('webpack-hmr') ||
         message.includes('webpack') ||
         message.includes('HMR')) &&
        (message.includes('failed') || 
         message.includes('Invalid frame header') || 
         message.includes('error') ||
         message.includes('Error'))
      ) {
        // Silently ignore these errors - they're harmless
        return;
      }
      
      // Log all other errors normally
      originalError.apply(console, args);
    };
    
    // Also catch warnings
    console.warn = function(...args: any[]) {
      const message = args.join(' ');
      
      // Suppress WebSocket warnings - catch various formats
      if (
        (message.includes('WebSocket') || 
         message.includes('webpack-hmr') ||
         message.includes('webpack') ||
         message.includes('HMR') ||
         message.includes('ws://') ||
         message.includes('wss://')) &&
        (message.includes('failed') || 
         message.includes('error') ||
         message.includes('Error'))
      ) {
        return;
      }
      
      originalWarn.apply(console, args);
    };
    
    // Also catch WebSocket errors at the global level
    const handleError = (event: ErrorEvent) => {
      const message = event.message || event.error?.message || '';
      if (
        (message.includes('WebSocket') || 
         message.includes('webpack-hmr') ||
         message.includes('webpack') ||
         message.includes('HMR') ||
         message.includes('ws://') ||
         message.includes('wss://')) &&
        (message.includes('failed') || 
         message.includes('Invalid frame header') ||
         message.includes('error') ||
         message.includes('Error'))
      ) {
        event.preventDefault();
        return false;
      }
    };
    
    window.addEventListener('error', handleError, true);
    
    // Cleanup
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError, true);
    };
  }, []);
  
  return (
    <Script
      id="suppress-hmr-errors"
      strategy="beforeInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          (function() {
            // Store original methods
            const originalError = console.error;
            const originalWarn = console.warn;
            
            // Override console.error IMMEDIATELY
            console.error = function(...args) {
              const message = args.join(' ');
              // Suppress WebSocket HMR errors - catch various formats
              if (
                (message.includes('WebSocket connection') || 
                 message.includes('WebSocket') || 
                 message.includes('web-socket') ||
                 message.includes('ws://') ||
                 message.includes('wss://')) &&
                (message.includes('_next/webpack-hmr') || 
                 message.includes('webpack-hmr') ||
                 message.includes('webpack') ||
                 message.includes('HMR')) &&
                (message.includes('failed') || 
                 message.includes('Invalid frame header') || 
                 message.includes('error') ||
                 message.includes('Error'))
              ) {
                return; // Silently ignore
              }
              originalError.apply(console, args);
            };
            
            // Override console.warn
            console.warn = function(...args) {
              const message = args.join(' ');
              if (
                (message.includes('WebSocket') || message.includes('webpack-hmr')) &&
                (message.includes('failed') || message.includes('error'))
              ) {
                return;
              }
              originalWarn.apply(console, args);
            };
            
            // Catch global errors
            window.addEventListener('error', function(event) {
              const message = event.message || (event.error && event.error.message) || '';
              if (
                (message.includes('WebSocket') || 
                 message.includes('webpack-hmr') ||
                 message.includes('webpack') ||
                 message.includes('HMR') ||
                 message.includes('ws://') ||
                 message.includes('wss://')) &&
                (message.includes('failed') || 
                 message.includes('Invalid frame header') ||
                 message.includes('error') ||
                 message.includes('Error'))
              ) {
                event.preventDefault();
                return false;
              }
            }, true);
            
            // Also catch unhandled promise rejections
            window.addEventListener('unhandledrejection', function(event) {
              const message = event.reason?.message || String(event.reason || '');
              if (
                (message.includes('WebSocket') || 
                 message.includes('webpack-hmr') ||
                 message.includes('webpack') ||
                 message.includes('HMR') ||
                 message.includes('ws://') ||
                 message.includes('wss://')) &&
                (message.includes('failed') || 
                 message.includes('Invalid frame header') ||
                 message.includes('error') ||
                 message.includes('Error'))
              ) {
                event.preventDefault();
                return false;
              }
            });
          })();
        `,
      }}
    />
  );
}

